import { ChangeEvent, useState } from 'react'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import TextField from '@mui/material/TextField'
import { rule, validator } from '../../../../utility/validation'
import { EntitiesTypes, getEntityTitle } from '../../../../view/admin/ad-management/EntitiesTabs'

interface IProps {
  isDisabled: boolean
  type: EntitiesTypes
  entityId: string
  entityName: string
  onEntityId: (data: { value: string; isValid: boolean }) => void
  onEntityName: (data: { value: string; isValid: boolean }) => void
}

export const EntityData = ({ isDisabled, type, entityId, entityName, onEntityId, onEntityName }: IProps) => {
  const titleName = getEntityTitle(type)
  const checkRequired = () => {
    if (type === EntitiesTypes.referral_types || type === EntitiesTypes.omit_measures) {
      return false
    } else {
      return true
    }
  }
  const [errorEntityId, setErrorEntityId] = useState('')
  const [errorEntityName, setErrorEntityName] = useState('')
  const getLabels = (titleName: string) => {
    if (titleName === 'Referral Type') {
      return { idLabel: 'Name', nameLabel: 'Description' }
    }
    if (titleName === 'Measure') {
      return { idLabel: 'Measure Name', nameLabel: 'Description' }
    }
    if (titleName === 'Report') {
      return { idLabel: 'Report Name', nameLabel: 'Description' }
    }
    return { idLabel: `${titleName} ID`, nameLabel: `${titleName} Name` }
  }

  const { idLabel, nameLabel } = getLabels(titleName)

  const renderEntityId = () => {
    const validationConfig: Record<string, { rules: rule[]; message: string }> = {
      pharmacies: {
        rules: [rule.min1symbol, rule.max16symbols, rule['latinLettersAndNumbersCaps']],
        message: 'ID is required',
      },
      distributors: {
        rules: [rule.min1symbol, rule.max32symbols, rule['latinLettersAndNumbersCaps']],
        message: 'ID is required',
      },
      referral_types: {
        rules: [rule.min1symbol, rule.max64symbols, rule.rule18],
        message: 'Referral Type Name is required',
      },
      omit_measures: {
        rules: [rule.min1symbol, rule.max32symbols, rule['lettersNumbersSpaces']],
        message: 'Measure Name is required',
      },
      reports: {
        rules: [rule.min1symbol, rule.max64symbols, rule.reportName],
        message: 'Report Name is required',
      },
    }
    const handleEntityId = (value: string) => {
      const { rules, message } = validationConfig[type] || { rules: [], message: 'Invalid type' }
      const validationMessage = validator(value, rules, message)
      setErrorEntityId(validationMessage)
      onEntityId({
        value,
        isValid: validationMessage === '',
      })
    }

    return (
      <TextField
        required
        margin='normal'
        fullWidth
        label={idLabel}
        value={entityId}
        disabled={isDisabled}
        error={errorEntityId !== ''}
        helperText={errorEntityId}
        onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => handleEntityId(value)}
        inputProps={{ 'data-testid': `${type}-id` }}
        placeholder={idLabel}
        InputLabelProps={{ shrink: true }}
      />
    )
  }

  const renderEntityName = () => {
    const validationConfig: Record<string, { rules: rule[]; message: string; required: boolean }> = {
      'Referral Type': {
        rules: [rule.max320symbols, rule.rule16],
        message: '',
        required: false,
      },
      Measure: {
        rules: [rule.max128symbols, rule.rule15],
        message: '',
        required: false,
      },
      Report: {
        rules: [rule.min1symbol, rule.max320symbols, rule.rule16],
        message: 'Description is required',
        required: true,
      },
      default: {
        rules: [rule.min1symbol, rule.max128symbols, rule.rule15],
        message: 'Name is required',
        required: true,
      },
    }

    const { rules, message, required } = validationConfig[titleName] || validationConfig.default

    const handleEntityName = (value: string) => {
      let validationMessage = ''
      if (required || value.trim().length > 0) {
        validationMessage = validator(value, rules, message)
      }
      setErrorEntityName(validationMessage)
      onEntityName({
        value,
        isValid: validationMessage === '',
      })
    }

    return (
      <TextField
        required={checkRequired()}
        margin='normal'
        fullWidth
        label={nameLabel}
        value={entityName}
        disabled={isDisabled}
        error={errorEntityName !== ''}
        helperText={errorEntityName}
        onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => handleEntityName(value)}
        inputProps={{ 'data-testid': `${type}-name` }}
        placeholder={nameLabel}
        InputLabelProps={{ shrink: true }}
      />
    )
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant='h3'>General Data</Typography>
        <Grid container>
          <Grid
            item
            xs={5}
          >
            {renderEntityId()}
          </Grid>
          <Grid
            item
            xs={2}
          />
          <Grid
            item
            xs={5}
          >
            {renderEntityName()}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
